"use client";
import {
	faBook,
	faBookOpen,
	faChessKnight,
	faDragon,
	faGamepad,
	faLink,
	faScroll,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

const DnDPage = () => {
	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/sistemas/sistemas_encabezado.webp')" }}
			>
				<div className="absolute inset-0 bg-black/60"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">
							Dungeons & Dragons en Madrid
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-12"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">
							El juego que lleva revolucionando la cultura de los juegos de mesa desde 1974, Dungeons &
							Dragons es el juego de rol de mesa por excelencia, centrado en la fantasía épica, el combate
							táctico por turnos y la toma de decisiones con dados. Los jugadores encarnan héroes con
							habilidades únicas que luchan contra dragones, exploran mazmorras y salvan reinos enteros. Su
							sistema equilibra estrategia y narrativa, permitiendo jugadas épicas, hechizos espectaculares y
							momentos de gloria que se recuerdan durante años.
						</strong>
					</motion.p>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.6 }}
						className="prose prose-invert max-w-none"
					>
						{/* Historical Context and Evolution */}
						<div className="bg-gray-900 rounded-lg p-8 mb-12 border-2 border-red-900/50">
							<h2 className="text-3xl font-amarante headerFont mb-6" style={{ textAlign: "left" }}>
								<FontAwesomeIcon icon={faDragon} className="mr-3 text-red-500" />
								La Historia de D&D
							</h2>
							<p className="text-gray-300 mb-4">
								Creado por Gary Gygax y Dave Arneson, <em>Dragones & Mazmorras</em> no es solo un juego, es
								una revolución en el mundo de los juegos de mesa. Desde mediados de los 70, ha transformado la
								forma en que imaginamos y jugamos historias de fantasía.
							</p>
							<p className="text-gray-300">
								La quinta edición provocó un resurgimiento masivo de popularidad, impulsado por series como
								Stranger Things y Critical Role, atrayendo a una nueva generación de jugadores.{" "}
							</p>
						</div>

						{/* Game Characteristics */}
						<div className="grid md:grid-cols-2 gap-8 mb-12">
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faChessKnight} className="mr-3 text-red-500" />
									Filosofía del Juego
								</h3>
								<p className="text-gray-300">
									D&D está en un punto medio perfecto: reglas lo suficientemente simples para principiantes,
									pero con la libertad y profundidad que los jugadores veteranos aprecian. Los jugadores
									tienen espacio para ser creativos sin sentirse abrumados por reglas complicadas.
								</p>
							</div>
							<div className="bg-gray-800 rounded-lg p-6">
								<h3 className="text-2xl font-amarante text-red-400 mb-4">
									<FontAwesomeIcon icon={faBook} className="mr-3 text-red-500" />
									Narrativa Flexible
								</h3>
								<p className="text-gray-300">
									A diferencia de ediciones anteriores, el Director de Juego (DM) ahora puede premiar a los
									jugadores por acciones creativas como negociación, resolución de puzzles o trabajo en
									equipo, no solo por combatir.
								</p>
							</div>
						</div>

						{/* Detailed Exploration */}
						<div className="space-y-8">
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-left mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faScroll} className="mr-3 text-red-500" />
									Universo y Personalización
								</h3>
								<p className="text-gray-300 mb-4">
									Las aventuras se desarrollan normalmente en los Reinos Olvidados (Forgotten Realms), un
									mundo inspirado originalmente en la Tierra Media de Tolkien. D&D ofrece una increíble
									variedad de razas, desde clásicos como humanos, elfos y enanos, hasta opciones únicas como
									Tieflings infernales y Warforged mecánicos.
								</p>
								<p className="text-gray-300">
									Cada personaje tiene una clase que determina sus habilidades y un alineamiento que indica su
									brújula moral. La historia previa del personaje es ahora un elemento crucial de la
									interpretación.
								</p>
							</div>

							{/* Recommended Campaigns */}
							<div className="bg-gray-900 rounded-lg p-8">
								<h3 className="text-2xl font-amarante text-red-400 mb-6">Campañas Recomendadas</h3>
								<div className="grid md:grid-cols-2 gap-6">
									<div>
										<h4 className="text-xl text-red-300 mb-4">Campañas Cortas (16 horas)</h4>
										<ul className="list-disc pl-5 text-gray-300 space-y-2">
											<li>
												<strong>La Mina perdida de Phandelver</strong>: Introducción perfecta ak mundo de
												Dragones & Mazmorras con laberintos, tesoros y monstruos legendarios. Además puede
												usarse de putno de partida para la campaña Phandelver y más allá: El Obelisco
												Despedazado
												<a
													href="https://tesorosdelamarca.com/producto/phandelver-y-mas-alla-el-obelisco-despedazado/"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 ml-2 hover:underline"
												>
													<FontAwesomeIcon icon={faLink} className="mr-1" />
													Bajemos a la Mina de Phandelver
												</a>
											</li>
											<li>
												<strong>El laboratorio perdido de Kwalish</strong>: Aventura steampunk mágica sobre un
												legendario inventor.
												<a
													href="https://www.dmsguild.com/product/258047/Lost-Laboratory-of-Kwalish-5e"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 ml-2 hover:underline"
												>
													<FontAwesomeIcon icon={faLink} className="mr-1" />
													Detalles
												</a>
											</li>
										</ul>
									</div>
									<div>
										<h4 className="text-xl text-red-300 mb-4">Campañas Extensas (100+ horas)</h4>
										<ul className="list-disc pl-5 text-gray-300 space-y-2">
											<li>
												<strong>La Maldición de Strahd</strong>: Horror gótico en el castillo de Ravenloft con
												el poderoso Conde Strahd von Zarovich como antagonista.
												<a
													href="https://dnd.wizards.com/es/products/curse-strahd-revamped"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 ml-2 hover:underline"
												>
													<FontAwesomeIcon icon={faLink} className="mr-1" />
													Más información
												</a>
											</li>
											<li>
												<strong>La Tumba de la Aniquilación</strong>: Aventura en las tierras salvajes de
												Chult con volcanes, selvas y ruinas de reinos desaparecidos... ¡y carreras de
												dinosaurios!
												<a
													href="https://dracotienda.com/dungeonsdragons-nueva-edicion/21005-dungeons-dragons-la-tumba-de-la-aniquilacion.html"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 ml-2 hover:underline"
												>
													<FontAwesomeIcon icon={faLink} className="mr-1" />
													Explorar la jungla indómita
												</a>
											</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Getting Started */}
							<div>
								<h3
									className="text-2xl font-amarante headerFont text-red-500 mb-4"
									style={{ textAlign: "left" }}
								>
									<FontAwesomeIcon icon={faBookOpen} className="mr-3 text-red-500" />
									¿Cómo Empezar?
								</h3>
								<div className="bg-gray-800 rounded-lg p-6 text-gray-300">
									<p className="mb-4">Para iniciarse, te recomendamos:</p>
									<ul className="list-disc pl-5 space-y-2">
										<li>
											<strong>Por supuesto,</strong> la manera más fácil de adentrarte en el mundo de Dragones
											y Mazmorras es con nuestra Aventura de Introducción :
											<strong>"Mareando el Carnero"</strong> - Una partida completa de 4 horas llena de
											acción, risas, y animales parlantes.
											<a
												href="/product/mareando-el-carnero"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Detalles de la Aventura
											</a>
										</li>
										<li>
											<strong>Caja de Inicio</strong>: La opción más recomendada, incluyendo la edición de
											Stranger Things.
											<div className="flex space-x-4 mt-2">
												<a
													href="https://dnd.wizards.com/es/products/starter-set"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 hover:underline"
												>
													<FontAwesomeIcon icon={faGamepad} className="mr-2" />
													Caja de Inicio Original
												</a>
												<a
													href="https://juegosdelamesaredonda.com/7891-dungeons-and-dragons-starter-set-caja-de-inicio-edicion-stranger-things-castellano-5010993642595.html"
													target="_blank"
													rel="noopener noreferrer"
													className="text-red-400 hover:underline"
												>
													<FontAwesomeIcon icon={faGamepad} className="mr-2" />
													Edición Stranger Things
												</a>
											</div>
										</li>
										<li>
											<strong>Partidas Abiertas</strong>: Busca eventos en tiendas locales como ContemplaRol
											en Madrid.
											<a
												href="https://www.meetup.com/es-ES/contemplarol/"
												target="_blank"
												rel="noopener noreferrer"
												className="text-red-400 ml-2 hover:underline"
											>
												<FontAwesomeIcon icon={faLink} className="mr-1" />
												Eventos de ContemplaRol
											</a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</motion.div>

					{/* CTA Section */}
					<motion.div
						className="mt-16 text-center"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<h3 className="text-3xl font-amarante text-red-400 mb-6">¿Listo para tu primera aventura?</h3>
						<motion.a
							href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, estoy interesado/a en jugar Dragones & Mazmorras en Madrid. ¿Podéis ayudarme?")}`}
							className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
							whileHover={{
								scale: 1.1,
								boxShadow: "0 0 15px #f93d3d",
								backgroundColor: "white",
								color: "black",
								fontWeight: "900",
								fontStyle: "italic",
							}}
							whileTap={{ scale: 0.98 }}
							target="_blank"
							rel="noopener noreferrer"
						>
							Comenzar mi aventura
						</motion.a>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default DnDPage;
